import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { referralsApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, LoadingBlock, TextInput } from './ui.tsx';

/**
 * Settings → Refer & Earn.
 *
 * The screen has one job before any other: put the link in front of somebody
 * and make it one press to share. Everything else — the history, the credit
 * balance, the invitation composer — is below that, because a referral program
 * people have to hunt for is a referral program nobody uses.
 *
 * The invitation composer is fully editable, and deliberately so: a note from
 * a person who actually uses the product is worth more than any copy shipped
 * here. The link is appended server-side, so an edit that deletes the
 * placeholder still sends a working, attributed invitation.
 *
 * The link and the history are for the whole company; renaming the program and
 * sending invitations in the company's name are `company:write`, which the role
 * matrix makes the owner's. Both controls are therefore withheld rather than
 * shown and refused — a button that can only ever return 403 is a lie, the same
 * rule the settings sections next door follow.
 */
export default function ReferralsSection() {
  const { t } = useTranslation();
  const { can } = useSession();
  const canManage = can('company:write');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const overview = useQuery({ queryKey: ['referrals'], queryFn: () => referralsApi.overview() });
  const history = useQuery({ queryKey: ['referral-history'], queryFn: () => referralsApi.history() });

  const invite = useMutation({
    mutationFn: () =>
      referralsApi.invite({
        recipientEmail: recipient,
        ...(subject.trim() ? { subject } : {}),
        ...(message.trim() ? { body: message } : {}),
      }),
    onSuccess: () => {
      setSent(true);
      setRecipient('');
      setError(null);
      void history.refetch();
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('referrals.sendError')),
  });

  const rename = useMutation({
    mutationFn: (displayName: string) => referralsApi.setDisplayName(displayName),
    onSuccess: () => void overview.refetch(),
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('referrals.saveError')),
  });

  if (overview.isLoading) return <LoadingBlock />;
  const data = overview.data;
  // Only the program being switched off hides the section. A missing LINK does
  // not: the code is created by an owner's visit, so a manager or a read-only
  // user who reaches this screen first has no link yet — and blanking the whole
  // section for them would take away the terms, the balance and the history
  // too, all of which the route still returns precisely because reading the
  // program was never the part that needed gating.
  if (!data || !data.enabled) return null;

  const copy = async () => {
    await navigator.clipboard?.writeText(data.url ?? '');
    setCopied(true);
  };

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold">{t('referrals.title')}</h2>
        <p className="text-sm text-slate-600">
          {t('referrals.intro', { days: data.referredTrialDays, months: data.referrerRewardMonths })}
        </p>
      </header>

      {error ? <Alert tone="error">{error}</Alert> : null}

      <div className="rounded border border-slate-200 p-4">
        {data.url ? (
          <>
            <div className="mt-1 flex flex-wrap items-end gap-2">
              <TextInput
                id="referral-link"
                label={t('referrals.yourLink')}
                readOnly
                value={data.url}
                className="min-w-0 flex-1"
              />
              <Button type="button" onClick={() => void copy()}>
                {copied ? t('common.copied') : t('common.copyLink')}
              </Button>
            </div>
            {/* Inside the link's branch because the display name is the code's:
                with no code there is no name to show, and `shownAs` would
                render an empty quotation. */}
            <p className="mt-2 text-sm text-slate-600">
              {t('referrals.shownAs', { name: data.displayName })}{' '}
              {canManage ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    const next = window.prompt(t('referrals.displayNamePrompt'), data.displayName ?? '');
                    if (next && next.trim()) rename.mutate(next.trim());
                  }}
                >
                  {t('common.edit')}
                </Button>
              ) : null}
            </p>
          </>
        ) : (
          // Not an error and not an invitation to act: whoever reads this is
          // almost certainly somebody who cannot create the code. One wording
          // serves both roles because an owner scarcely reaches this state —
          // their own read is what creates the code — and if they somehow do,
          // this still tells them the true thing.
          <p className="text-sm text-slate-600">{t('referrals.linkNotReady')}</p>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t('referrals.statTotal')} value={data.stats?.total ?? 0} />
        <Stat label={t('referrals.statPending')} value={data.stats?.pending ?? 0} />
        <Stat label={t('referrals.statQualified')} value={data.stats?.qualified ?? 0} />
        <Stat label={t('referrals.statAvailable')} value={data.credits?.availableMonths ?? 0} />
      </dl>

      {canManage ? (
        <div className="rounded border border-slate-200 p-4">
          <h3 className="font-semibold">{t('referrals.inviteTitle')}</h3>
          {sent ? <Alert tone="success">{t('referrals.sent')}</Alert> : null}
          <div className="mt-2 space-y-2">
            <TextInput
              id="referral-recipient"
              label={t('referrals.recipient')}
              type="email"
              value={recipient}
              onChange={(event) => setRecipient(event.target.value)}
            />
            <TextInput
              id="referral-subject"
              label={t('referrals.subject')}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
            <label className="block text-sm" htmlFor="referral-message">
              {t('referrals.message')}
            </label>
            <textarea
              id="referral-message"
              className="w-full rounded border border-slate-300 p-2 text-sm"
              rows={6}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t('referrals.messagePlaceholder')}
            />
            <p className="text-xs text-slate-600">{t('referrals.linkAppended')}</p>
            <Button type="button" onClick={() => invite.mutate()} disabled={invite.isPending || !recipient}>
              {t('referrals.send')}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="rounded border border-slate-200 p-4">
        <h3 className="font-semibold">{t('referrals.historyTitle')}</h3>
        {(history.data?.referrals ?? []).length === 0 ? (
          <p className="mt-1 text-sm text-slate-600">{t('referrals.noHistory')}</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100 text-sm">
            {(history.data?.referrals ?? []).map((referral) => (
              <li key={referral.id} className="flex justify-between py-2">
                {/* A status and a date, and nothing that identifies the
                    referred business — they are somebody else's customer, not
                    a record the referrer is owed. */}
                <span>{t(`referrals.status.${referral.status}`)}</span>
                <span className="text-slate-600">{new Date(referral.createdAt).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-slate-200 p-3">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-xl font-semibold">{value}</dd>
    </div>
  );
}
